import { useState, type ComponentType } from 'react';
import {
  Download,
  Printer,
  Send,
  MousePointerClick,
  ShieldAlert,
  Percent,
  BarChart3,
  ShieldCheck,
  Building2,
  Database,
  Mail,
  Check,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { PageHeader, Card, StatCard, EmptyState, Badge, cx } from '../components/ui';
import { funnelFromEvents, countersFromFunnel, type EventLite } from '../lib/aggregate';
import { EventLogSection } from './EventLogPage';

// Reporting (PRD §6.4/§8.4/§2.9): core reports from the Events + Usage APIs, with CSV/print export.
// "Reporting integrations" (SIEM/BI/Slack push) are scaffolded request-access cards for now.
interface RptEvent extends EventLite {
  campaign_id: string;
}
interface Usage {
  period: string;
  total_units: number;
  by_org: { org_id: string; units: number }[];
}

function downloadCsv(name: string, rows: (string | number)[][]) {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const PUSH: {
  key: string;
  name: string;
  desc: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  {
    key: 'splunk',
    name: 'Splunk / SIEM',
    desc: 'Stream report events to your SIEM.',
    icon: ShieldCheck,
  },
  {
    key: 'powerbi',
    name: 'Power BI',
    desc: 'Live dataset for executive dashboards.',
    icon: BarChart3,
  },
  {
    key: 'looker',
    name: 'Looker Studio',
    desc: 'Connected data source for reports.',
    icon: BarChart3,
  },
  { key: 'slack', name: 'Slack digest', desc: 'Scheduled summary to a channel.', icon: Send },
  {
    key: 'email',
    name: 'Scheduled email',
    desc: 'Weekly/monthly PDF to stakeholders.',
    icon: Mail,
  },
  { key: 's3', name: 'S3 / CSV drop', desc: 'Automated CSV export to a bucket.', icon: Database },
];

export function ReportingPage() {
  const { withOrg, selectedOrgId } = useOrg();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'events' ? 'events' : 'reports';
  const ev = useLoad(
    () => api.get<{ data: RptEvent[] }>(withOrg('/v1/events?limit=100')),
    [selectedOrgId],
  );
  const us = useLoad(() => api.get<Usage>(withOrg('/v1/usage')), [selectedOrgId]);
  const [requested, setRequested] = useState<Record<string, boolean>>({});

  const events = ev.data?.data ?? [];
  const counters = countersFromFunnel(funnelFromEvents(events));

  // Per-campaign breakdown (group client-side; pure aggregate over the events page).
  const byCampaign = new Map<string, RptEvent[]>();
  for (const e of events) {
    const arr = byCampaign.get(e.campaign_id) ?? [];
    arr.push(e);
    byCampaign.set(e.campaign_id, arr);
  }
  const rows = [...byCampaign.entries()].map(([cid, evs]) => {
    const f = funnelFromEvents(evs);
    return {
      campaign: cid,
      sent: f.sent,
      opened: f.opened,
      clicked: f.clicked,
      submitted: f.submitted_data,
      reported: f.reported,
    };
  });

  function exportCampaigns() {
    downloadCsv('campaign-report.csv', [
      ['Campaign', 'Sent', 'Opened', 'Clicked', 'Submitted', 'Reported'],
      ...rows.map((r) => [r.campaign, r.sent, r.opened, r.clicked, r.submitted, r.reported]),
    ]);
  }

  return (
    <div>
      <PageHeader
        title="Reporting"
        subtitle="Campaign, engagement, and per-Org reports across your simulations."
        actions={
          tab === 'reports' ? (
            <>
              <button onClick={exportCampaigns} className="btn-outline">
                <Download className="h-4 w-4" /> Export CSV
              </button>
              <button onClick={() => window.print()} className="btn-outline">
                <Printer className="h-4 w-4" /> Print / PDF
              </button>
            </>
          ) : undefined
        }
      />

      <div className="mb-6 flex gap-1 border-b border-line">
        {[
          { key: 'reports', label: 'Reports' },
          { key: 'events', label: 'Event Log' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setParams(t.key === 'reports' ? {} : { tab: t.key }, { replace: true })}
            className={cx(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'border-ink text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'events' ? (
        <EventLogSection />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Sent" value={String(counters.sent)} icon={Send} />
            <StatCard label="Clicked" value={String(counters.clicked)} icon={MousePointerClick} />
            <StatCard
              label="Click rate"
              value={`${Math.round(counters.clickRate * 100)}%`}
              icon={Percent}
            />
            <StatCard label="Reported" value={String(counters.reported)} icon={ShieldAlert} />
          </div>

          <div className="eyebrow mb-3 mt-8">Campaign performance</div>
          <Card className="overflow-hidden">
            {rows.length === 0 ? (
              <EmptyState
                icon={BarChart3}
                title="No campaign data yet"
                hint="Launch a campaign to populate reports."
              />
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-5 py-3">Campaign</th>
                    <th className="px-5 py-3 text-right">Sent</th>
                    <th className="px-5 py-3 text-right">Opened</th>
                    <th className="px-5 py-3 text-right">Clicked</th>
                    <th className="px-5 py-3 text-right">Submitted</th>
                    <th className="px-5 py-3 text-right">Reported</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((r) => (
                    <tr key={r.campaign}>
                      <td className="px-5 py-3 font-mono text-xs text-muted">{r.campaign}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-ink">{r.sent}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-muted">{r.opened}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-ink">{r.clicked}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-ink">{r.submitted}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-muted">{r.reported}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <div className="eyebrow mb-3 mt-8">Usage by Org</div>
          <Card>
            {(us.data?.by_org ?? []).length === 0 ? (
              <EmptyState icon={Building2} title="No metered usage yet" />
            ) : (
              <ul className="divide-y divide-line">
                {us.data!.by_org.map((o) => (
                  <li
                    key={o.org_id}
                    className="flex items-center justify-between px-5 py-3 text-sm"
                  >
                    <span className="font-mono text-xs text-muted">{o.org_id}</span>
                    <span className="font-medium tabular-nums text-ink">{o.units} units</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <div className="eyebrow mb-3 mt-8">Reporting integrations</div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PUSH.map((p) => {
              const Icon = p.icon;
              const done = requested[p.key];
              return (
                <Card key={p.key} className="flex flex-col p-5">
                  <div className="flex items-start justify-between">
                    <span className="grid h-9 w-9 place-items-center rounded-lg border border-line text-ink">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    <Badge tone="muted">Available</Badge>
                  </div>
                  <div className="mt-3 font-display text-lg text-ink">{p.name}</div>
                  <div className="mt-1 text-sm text-muted">{p.desc}</div>
                  <div className="flex-1" />
                  <button
                    onClick={() => setRequested((r) => ({ ...r, [p.key]: true }))}
                    disabled={done}
                    className="btn-outline mt-4 w-full"
                  >
                    {done ? (
                      <>
                        <Check className="h-4 w-4" /> Requested
                      </>
                    ) : (
                      'Request access'
                    )}
                  </button>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
